import React, { useState } from 'react';
import { Upload, X, FileText, Image as ImageIcon, AlertCircle } from 'lucide-react';

interface Step4Props {
  data: any;
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const Step4PhotosDocuments: React.FC<Step4Props> = ({
  data,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    photos: data.photos || [],
    master_plan_url: data.master_plan_url || '',
    brochure_url: data.brochure_url || '',
    typePhotos: data.typePhotos || {
      f2: [],
      f3: [],
      f4: [],
      f5: [],
    },
  });

  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    setUploading(true);
    const files = Array.from(e.target.files);

    try {
      const uploadedUrls: string[] = [];

      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('photo', file);

        const response = await fetch('/api/properties/upload-photo', {
          method: 'POST',
          body: formDataUpload,
        });

        const data = await response.json();
        uploadedUrls.push(data.url);
      }

      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...uploadedUrls],
      }));
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload des photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_: string, i: number) => i !== index),
    }));
  };

  const handleTypePhotoUpload = async (type: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    setUploading(true);
    const files = Array.from(e.target.files);

    try {
      const uploadedUrls: string[] = [];

      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('photo', file);

        const response = await fetch('/api/properties/upload-photo', {
          method: 'POST',
          body: formDataUpload,
        });

        const data = await response.json();
        uploadedUrls.push(data.url);
      }

      setFormData(prev => ({
        ...prev,
        typePhotos: {
          ...prev.typePhotos,
          [type]: [...(prev.typePhotos[type] || []), ...uploadedUrls],
        },
      }));
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload des photos');
    } finally {
      setUploading(false);
    }
  };

  const removeTypePhoto = (type: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      typePhotos: {
        ...prev.typePhotos,
        [type]: prev.typePhotos[type].filter((_: string, i: number) => i !== index),
      },
    }));
  };

  const handleDocumentUpload = async (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    setUploading(true);
    const file = e.target.files[0];

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('document', file);

      const response = await fetch('/api/properties/upload-document', {
        method: 'POST',
        body: formDataUpload,
      });

      const data = await response.json();

      setFormData(prev => ({
        ...prev,
        [field]: data.url,
      }));
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(formData);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Photos et documents
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Ajoutez des photos pour rendre votre promotion attractive
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Photos générales de la promotion
        </h3>

        <label className="block border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer transition-colors">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            className="hidden"
            disabled={uploading}
          />

          <div className="flex flex-col items-center">
            <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" />

            {uploading ? (
              <p className="text-gray-600 dark:text-gray-400">
                Upload en cours...
              </p>
            ) : (
              <>
                <p className="text-gray-900 dark:text-white font-medium mb-1">
                  Cliquez pour ajouter des photos
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  ou glissez-déposez vos fichiers ici
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                  Max 10 photos • JPG, PNG • Max 5MB par photo
                </p>
              </>
            )}
          </div>
        </label>

        {formData.photos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {formData.photos.map((url: string, index: number) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Photos par type d'appartement (optionnel)
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Ajoutez des photos spécifiques pour chaque type de logement
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {['f2', 'f3', 'f4', 'f5'].map(type => (
            <div key={type} className="border dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white uppercase">
                  {type}
                </h4>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.typePhotos[type]?.length || 0} photo(s)
                </span>
              </div>

              <label className="block border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 cursor-pointer transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleTypePhotoUpload(type, e)}
                  className="hidden"
                  disabled={uploading}
                />

                <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  + Ajouter photos
                </p>
              </label>

              {formData.typePhotos[type]?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {formData.typePhotos[type].map((url: string, index: number) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`${type} ${index + 1}`}
                        className="w-full h-20 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeTypePhoto(type, index)}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Documents (optionnel)
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Plan de masse
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleDocumentUpload('master_plan_url', e)}
                  className="hidden"
                  disabled={uploading}
                />
                <FileText className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.master_plan_url ? 'Changer le fichier' : 'Choisir un fichier'}
                </span>
              </label>

              {formData.master_plan_url && (
                <a
                  href={formData.master_plan_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-sm"
                >
                  Voir le fichier
                </a>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Brochure PDF
            </label>
            <div className="flex items-center space-x-3">
              <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleDocumentUpload('brochure_url', e)}
                  className="hidden"
                  disabled={uploading}
                />
                <FileText className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formData.brochure_url ? 'Changer le fichier' : 'Choisir un fichier'}
                </span>
              </label>

              {formData.brochure_url && (
                <a
                  href={formData.brochure_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg text-sm"
                >
                  Voir le fichier
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4">
        <div className="flex">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Les photos peuvent être ajoutées ou modifiées plus tard depuis la page de gestion de la promotion
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          Retour
        </button>

        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Suivant : Publication
        </button>
      </div>
    </form>
  );
};

export default Step4PhotosDocuments;
